/**
 * Facility heat map.
 *
 * Ported from the Sounds For Seniors dashboard's Facility Map
 * (monday-psql-sync/public/app.js) so the two render the same operation the
 * same way: Leaflet, OpenStreetMap tiles, one circle marker per facility sized
 * by patient volume. The marker geometry and colours are carried over
 * deliberately — someone who knows the SFS map should recognise this one.
 *
 * What is added here:
 *   - a density (heat) layer, weighted by patient volume
 *   - provider home markers and their served facilities, for route planning
 *   - a bounds guard, so one bad coordinate cannot blow out the viewport
 *
 * Leaflet is used directly rather than through react-leaflet. The heat plugin
 * attaches itself to the `L` namespace and expects an imperative map instance;
 * wrapping that in react-leaflet's declarative model would mean a custom
 * component that reaches for the underlying instance anyway.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.heat';

export type GeoFacility = {
  facility_id: string;
  facility_name: string;
  facility_type_code?: string | null;
  region?: string | null;
  lat: number;
  lng: number;
  territory_status?: string | null;
  patient_count: number;
  active_patient_count: number;
  hearing_aids_dispensed_count: number;
  total_sale_value?: number | string | null;
  /** Present only on a provider-scoped map. */
  completed_visit_count?: number;
  scheduled_visit_count?: number;
};

export type GeoProvider = {
  employee_id: string;
  first_name: string;
  last_name: string;
  home_lat?: number | null;
  home_lng?: number | null;
  service_region?: string | null;
};

type Props = {
  facilities: GeoFacility[];
  providers?: GeoProvider[];
  /** Draw lines from a provider's home to each facility they work. */
  connectProviderHome?: boolean;
  showHeat?: boolean;
  height?: number | string;
};

// Florida, as a starting view for the moment before data arrives. Same centre
// and zoom the SFS dashboard uses.
const FLORIDA_CENTER: L.LatLngExpression = [28.5, -81.5];
const FLORIDA_ZOOM = 6.3;

const fmt = new Intl.NumberFormat('en-US');
const fmtMoney = new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', maximumFractionDigits: 0,
});

/** Escape before interpolating into a Leaflet popup: these strings are data. */
function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/** Carried over from the SFS dashboard so marker sizes match between the two. */
const markerRadius = (patientCount: number) =>
  Math.max(4, Math.min(22, Math.sqrt(patientCount || 0) * 2.2));

const SERVED = '#2B6E62';   // facility with patients — SFS teal
const UNSERVED = '#A66A22'; // geo-anchored, no patients yet — SFS amber
const PROVIDER = '#1F3A5F'; // provider home — the eMerge wordmark slate

export default function FacilityHeatMap({
  facilities,
  providers = [],
  connectProviderHome = false,
  showHeat = true,
  height = 560,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  // Everything drawn from data lives here so a re-render clears exactly what
  // it drew, rather than tearing down and rebuilding the whole map (which
  // would lose the user's pan and zoom on every prop change).
  const layerRef = useRef<L.LayerGroup | null>(null);
  const heatRef = useRef<L.Layer | null>(null);

  const [ready, setReady] = useState(false);

  const locatedProviders = useMemo(
    () => providers.filter((p) => typeof p.home_lat === 'number' && typeof p.home_lng === 'number'),
    [providers]
  );

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current, {
      center: FLORIDA_CENTER,
      zoom: FLORIDA_ZOOM,
      scrollWheelZoom: false, // a map inside a scrolling page must not hijack the wheel
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(map);

    // Click to enable the wheel, so zooming is possible but never accidental.
    map.on('click', () => map.scrollWheelZoom.enable());
    map.on('mouseout', () => map.scrollWheelZoom.disable());

    mapRef.current = map;
    layerRef.current = L.layerGroup().addTo(map);
    setReady(true);

    return () => { map.remove(); mapRef.current = null; };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!ready || !map || !layer) return;

    layer.clearLayers();
    if (heatRef.current) { map.removeLayer(heatRef.current); heatRef.current = null; }

    const bounds: L.LatLngExpression[] = [];

    for (const f of facilities) {
      const color = f.patient_count > 0 ? SERVED : UNSERVED;
      const marker = L.circleMarker([f.lat, f.lng], {
        radius: markerRadius(f.patient_count),
        color,
        fillColor: color,
        fillOpacity: 0.55,
        weight: 1.5,
      });

      const visitLine = typeof f.completed_visit_count === 'number'
        ? `Visits completed here: <strong>${fmt.format(f.completed_visit_count)}</strong><br/>`
        : '';

      marker.bindPopup(`
        <strong>${escapeHtml(f.facility_name)}</strong><br/>
        ${escapeHtml(f.facility_type_code ?? '—')} · ${escapeHtml(f.region ?? 'no region')}<br/>
        ${visitLine}
        Patients: ${fmt.format(f.patient_count)} (${fmt.format(f.active_patient_count)} active)<br/>
        Hearing aids dispensed: ${fmt.format(f.hearing_aids_dispensed_count)}<br/>
        Total sale value: ${fmtMoney.format(Number(f.total_sale_value) || 0)}
      `);
      marker.addTo(layer);
      bounds.push([f.lat, f.lng]);
    }

    // Density. Weighted by patient volume rather than by row, because twenty
    // facilities of six patients is a different operational picture from one
    // facility of five hundred, and an unweighted layer draws them the same.
    if (showHeat && facilities.length) {
      const maxPatients = Math.max(...facilities.map((f) => f.patient_count || 0), 1);
      const points = facilities.map((f) => [
        f.lat, f.lng, Math.max(0.15, (f.patient_count || 0) / maxPatients),
      ]);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      heatRef.current = (L as any).heatLayer(points, {
        radius: 34, blur: 26, maxZoom: 11, minOpacity: 0.25,
        gradient: { 0.2: '#A9C3B6', 0.5: '#5F9C88', 0.8: '#2B6E62', 1.0: '#1F3A5F' },
      }).addTo(map);
    }

    for (const p of locatedProviders) {
      const home: L.LatLngExpression = [p.home_lat as number, p.home_lng as number];

      // A square-ish diamond, so a provider is distinguishable from a facility
      // at a glance and not only by colour.
      L.marker(home, {
        icon: L.divIcon({
          className: '',
          html: `<div style="width:14px;height:14px;transform:rotate(45deg);background:${PROVIDER};border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.4)"></div>`,
          iconSize: [14, 14],
          iconAnchor: [7, 7],
        }),
      })
        .bindPopup(`<strong>${escapeHtml(`${p.first_name} ${p.last_name}`)}</strong><br/>${escapeHtml(p.service_region ?? 'no service region recorded')}`)
        .addTo(layer);

      bounds.push(home);

      if (connectProviderHome) {
        for (const f of facilities) {
          L.polyline([home, [f.lat, f.lng]], {
            color: PROVIDER, weight: 1, opacity: 0.25,
          }).addTo(layer);
        }
      }
    }

    // Only fit when there is something to fit to. Calling fitBounds with an
    // empty array throws.
    if (bounds.length) {
      map.fitBounds(L.latLngBounds(bounds), { padding: [32, 32], maxZoom: 12 });
    }
  }, [ready, facilities, locatedProviders, connectProviderHome, showHeat]);

  return (
    <div
      ref={containerRef}
      style={{ height, width: '100%', borderRadius: 10, overflow: 'hidden' }}
      role="application"
      aria-label="Facility map"
    />
  );
}
